import { defineStore } from 'pinia';
import { Ref } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { IProductDTO, ITeamLevelDTO, ProductDTO, TeamLevelDTO } from '@/dtos';
import {
  createProductAPI,
  deleteProductAPI,
  getAvailableProductTeamLevelsAPI,
  getProductsForYearAdminAPI,
  saveProductAPI,
} from '@/services/admin';
import { useUpdateStatusStore } from '../updateStatus';
import { useAuthStore } from '..';
import { getBranchYearProductsAPI } from '@/services';

export const useProductStore = defineStore('product', () => {
  const queryClient = useQueryClient();
  const statusStore = useUpdateStatusStore();
  const authStore = useAuthStore();

  const availableProductTeamLevelsQuery = (yearId: Ref<string>) => {
    return useQuery({
      queryKey: ['products-available', yearId.value],
      queryFn: async (): Promise<ITeamLevelDTO[]> => {
        if (!yearId.value) throw new Error('yearId not set');
        const ret = await getAvailableProductTeamLevelsAPI(yearId.value);
        const products = await Promise.all<ITeamLevelDTO[]>(
          ret.data.map((level) => new TeamLevelDTO(level)),
        );
        return products;
      },
      enabled: () => !!yearId.value && yearId.value != '',
    });
  };

  const productsForYearQuery = (yearId: Ref<string>) => {
    return useQuery({
      queryKey: ['products-year', yearId.value],
      queryFn: async (): Promise<IProductDTO[]> => {
        let ret;
        if (authStore.isAdmin && yearId.value) {
          ret = await getProductsForYearAdminAPI(yearId.value);
        } else {
          ret = await getBranchYearProductsAPI();
        }
        const products = await Promise.all<IProductDTO[]>(
          ret.data.map((product) => new ProductDTO(product)),
        );
        return products;
      },
      enabled: () => !!yearId.value && yearId.value != '',
    });
  };

  // Mutations
  const create = useMutation({
    mutationFn: ({ product, yearId }: { product: IProductDTO; yearId: string }) => {
      return createProductAPI(yearId, product);
    },
    onSuccess: (_data: IProductDTO, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-year', variables.yearId] });
      queryClient.invalidateQueries({ queryKey: ['products-available', variables.yearId] });
      statusStore.setSuccessStatus('product-create', 'success:create');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus('product-create', err);
    },
  });

  const update = useMutation({
    mutationFn: ({ product }: { product: IProductDTO; yearId: string }) => {
      return saveProductAPI(product);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-year', variables.yearId] });
      queryClient.invalidateQueries({ queryKey: ['products-available', variables.yearId] });
      statusStore.setSuccessStatus(`product-${variables.product.id}`, 'success:update');
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`product-${variables.product.id}`, err);
    },
  });

  const remove = useMutation({
    mutationFn: ({ productId }: { productId: string; yearId: string }) =>
      deleteProductAPI(productId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-year', variables.yearId] });
      queryClient.invalidateQueries({ queryKey: ['products-available', variables.yearId] });
      statusStore.setSuccessStatus(`product-${variables.productId}`, 'success:delete');
    },
    onError: (err: string, productId) => {
      statusStore.setErrorStatus(`product-${productId}`, err);
    },
  });

  return {
    availableProductTeamLevelsQuery,
    productsForYearQuery,
    create,
    update,
    remove,
  };
});
